import type { AuthConfigResponse } from '@iq/contracts/v1/auth';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { api } from './api';

export type AuthConfig = AuthConfigResponse;

/**
 * Public auth-mode descriptor from `GET /api/v1/auth/config`. Tells the
 * sign-in / sign-up routes and the Security settings panel whether to show the
 * Authentik SSO button (`ssoEnabled`) or the legacy email/password UI. The
 * server is the single source of truth (no separate frontend env flag). The
 * auth mode is fixed for the app's lifetime, so the query never goes stale.
 */
const authConfigQuery = {
  queryKey: ['auth', 'config'] as const,
  queryFn: async (): Promise<AuthConfig> => {
    const { data, error } = await api.GET('/auth/config');
    if (error) throw error;
    if (!data) throw new Error('Empty 200 body from /auth/config');
    return data;
  },
  staleTime: Number.POSITIVE_INFINITY,
};

/** React hook returning the auth-config query (data may be undefined while loading). */
export function useAuthConfig() {
  return useQuery(authConfigQuery);
}

/** Router `beforeLoad` / `loader` helper. Caches + re-uses the same query. */
export function ensureAuthConfig(client: QueryClient): Promise<AuthConfig> {
  return client.fetchQuery(authConfigQuery);
}
